import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { currentBranchId } from '../config/branchContext';

export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type Lang = 'de' | 'en';

export interface TimeSlot { open: string; close: string; }
export interface DaySchedule { closed: boolean; slots: TimeSlot[]; }
export interface ScheduleOverride {
  date: string;
  closed: boolean;
  slots?: TimeSlot[];
  reason: { de: string; en: string };
}
export interface BranchSchedule {
  weekly: Record<DayKey, DaySchedule>;
  overrides: ScheduleOverride[];
}

export const DAY_KEYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// JS Date.getDay(): 0=Sun..6=Sat. Map to our keys.
const JS_DAY_TO_KEY: Record<number, DayKey> = {
  0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat',
};

export const DEFAULT_SCHEDULE: BranchSchedule = {
  weekly: {
    mon: { closed: true, slots: [] },
    tue: { closed: false, slots: [{ open: '11:30', close: '14:30' }, { open: '17:00', close: '22:30' }] },
    wed: { closed: false, slots: [{ open: '11:30', close: '14:30' }, { open: '17:00', close: '22:30' }] },
    thu: { closed: false, slots: [{ open: '11:30', close: '14:30' }, { open: '17:00', close: '22:30' }] },
    fri: { closed: false, slots: [{ open: '11:30', close: '14:30' }, { open: '17:00', close: '23:00' }] },
    sat: { closed: false, slots: [{ open: '12:00', close: '23:00' }] },
    sun: { closed: false, slots: [{ open: '12:00', close: '22:30' }] },
  },
  overrides: [],
};

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m || 0);
}

// Closing time relative to the slot's open time. A close of '00:00'/'24:00'
// (or any value <= open, e.g. open 16:00 close 02:00) means the slot runs to
// the end of the day / past midnight, so it's treated as +24h. Fixes
// "open until 00:00" (Hürth Fri/Sat) reading as closed all evening.
function closeMinutes(open: string, close: string): number {
  const o = toMinutes(open);
  const c = toMinutes(close);
  return c <= o ? c + 24 * 60 : c;
}

function parseSchedule(data: any): BranchSchedule {
  const raw = data?.schedule as Partial<BranchSchedule> | undefined;
  if (!raw?.weekly) return DEFAULT_SCHEDULE;
  return {
    weekly: { ...DEFAULT_SCHEDULE.weekly, ...raw.weekly } as any,
    overrides: Array.isArray(raw.overrides) ? raw.overrides : [],
  };
}

// Resilient subscription:
// - Re-subscribes after transient errors (e.g. permission change at runtime)
// - Re-fetches when the tab becomes visible again
// - Falls back to one-shot getDocs if the listener errors
export function subscribeBranchSchedule(
  onChange: (s: BranchSchedule) => void,
): () => void {
  let cancelled = false;
  let unsub: (() => void) | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const connect = () => {
    if (cancelled) return;
    if (unsub) { try { unsub(); } catch {} unsub = null; }
    // Read THIS webshop's branch doc by id (was limit(1), which grabbed whatever
    // branch happened to be first and showed the wrong hours on multi-branch).
    const ref = doc(db, 'branches', currentBranchId());
    unsub = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        if (!snap.exists()) { onChange(DEFAULT_SCHEDULE); return; }
        onChange(parseSchedule(snap.data()));
      },
      (err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[branchSchedule] listener error, retrying in 4s:', err?.message || err);
        // Do a one-shot fetch so we still get the latest if the listener was denied mid-flight
        getDoc(ref).then((snap) => {
          if (cancelled) return;
          if (snap.exists()) onChange(parseSchedule(snap.data()));
        }).catch(() => {});
        if (retryTimer) clearTimeout(retryTimer);
        retryTimer = setTimeout(connect, 4000);
      },
    );
  };

  connect();

  // Re-fetch whenever the tab regains focus — handles stale listeners
  // when the user leaves the site open overnight and comes back.
  const onVisible = () => {
    if (cancelled) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      connect();
    }
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
  }

  return () => {
    cancelled = true;
    if (retryTimer) clearTimeout(retryTimer);
    if (unsub) { try { unsub(); } catch {} }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    }
  };
}

export async function loadBranchScheduleOnce(): Promise<BranchSchedule> {
  try {
    const snap = await getDoc(doc(db, 'branches', currentBranchId()));
    if (!snap.exists()) return DEFAULT_SCHEDULE;
    const data = snap.data() as any;
    const raw = data?.schedule as Partial<BranchSchedule> | undefined;
    if (!raw?.weekly) return DEFAULT_SCHEDULE;
    return {
      weekly: { ...DEFAULT_SCHEDULE.weekly, ...raw.weekly } as any,
      overrides: Array.isArray(raw.overrides) ? raw.overrides : [],
    };
  } catch {
    return DEFAULT_SCHEDULE;
  }
}

/**
 * Whether the branch is configured with real opening hours (a `schedule` field).
 * The webshop only *enforces* hours when this is true, matching the server —
 * so a branch without configured hours never blocks ordering.
 */
export async function branchHasSchedule(): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, 'branches', currentBranchId()));
    return !!(snap.exists() && (snap.data() as any)?.schedule?.weekly);
  } catch {
    return false;
  }
}

export interface OpenStatus {
  open: boolean;
  label: { de: string; en: string };
  override?: ScheduleOverride; // if today is an override
  nextOpen?: { at: Date; slot: TimeSlot }; // for "closed — reopens at X" hints
}

export function computeStatus(schedule: BranchSchedule, now: Date = new Date()): OpenStatus {
  const today = isoDate(now);
  const override = schedule.overrides.find((o) => o.date === today);
  const dayKey = JS_DAY_TO_KEY[now.getDay()];
  const nowMin = now.getHours() * 60 + now.getMinutes();

  // Today's effective slots + closed flag
  let closedToday = false;
  let slots: TimeSlot[] = [];
  if (override) {
    closedToday = override.closed || !(override.slots && override.slots.length > 0);
    slots = override.closed ? [] : (override.slots || schedule.weekly[dayKey].slots);
  } else {
    const d = schedule.weekly[dayKey];
    closedToday = d.closed;
    slots = d.slots;
  }

  let open = false;
  for (const s of slots) {
    if (nowMin >= toMinutes(s.open) && nowMin <= closeMinutes(s.open, s.close)) {
      open = true;
      break;
    }
  }

  const label = open
    ? { de: 'Jetzt geöffnet', en: 'Open now' }
    : closedToday && override
      ? {
          de: override.reason.de ? `Heute geschlossen — ${override.reason.de}` : 'Heute geschlossen',
          en: override.reason.en ? `Closed today — ${override.reason.en}` : 'Closed today',
        }
      : closedToday
        ? { de: 'Heute geschlossen', en: 'Closed today' }
        : { de: 'Momentan geschlossen', en: 'Currently closed' };

  return { open, label, override };
}

export interface PreorderOption { value: string; date: string; time: string; }

/**
 * Selectable pre-order slots for the next `days` days, `stepMin`-minute steps
 * inside each day's open slots, excluding anything less than ~30 min ahead.
 * `value` is a full ISO timestamp (UTC) safe to send to the server, which
 * re-derives Europe/Berlin local time when validating.
 */
export function preorderOptions(
  schedule: BranchSchedule, days = 7, stepMin = 30, now: Date = new Date(),
): PreorderOption[] {
  const out: PreorderOption[] = [];
  for (let off = 0; off < days; off++) {
    const day = new Date(now);
    day.setDate(day.getDate() + off);
    const iso = isoDate(day);
    const override = schedule.overrides.find((o) => o.date === iso);
    const dayKey = JS_DAY_TO_KEY[day.getDay()];
    let slots: TimeSlot[] = [];
    if (override) {
      if (override.closed) continue;
      slots = (override.slots && override.slots.length) ? override.slots : schedule.weekly[dayKey].slots;
    } else {
      const d = schedule.weekly[dayKey];
      if (d.closed) continue;
      slots = d.slots;
    }
    for (const s of slots) {
      const end = closeMinutes(s.open, s.close);
      for (let t = toMinutes(s.open); t <= end; t += stepMin) {
        const h = Math.floor(t / 60), m = t % 60;
        const cand = new Date(day);
        cand.setHours(h, m, 0, 0);
        if (cand.getTime() <= now.getTime() + 30 * 60000) continue; // ≥30 min ahead
        out.push({ value: cand.toISOString(), date: iso, time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}` });
      }
    }
  }
  return out;
}

export function nextOverride(schedule: BranchSchedule, days = 14, now: Date = new Date()): ScheduleOverride | null {
  const todayIso = isoDate(now);
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() + days);
  const cutoffIso = isoDate(cutoff);
  const upcoming = schedule.overrides
    .filter((o) => o.date >= todayIso && o.date <= cutoffIso && o.closed)
    .sort((a, b) => a.date.localeCompare(b.date));
  return upcoming[0] || null;
}

export function formatOverrideDate(dateIso: string, lang: Lang): string {
  try {
    const [y, m, d] = dateIso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return dateIso;
  }
}

export function dayLabel(k: DayKey, lang: Lang): string {
  const de: Record<DayKey, string> = { mon: 'Montag', tue: 'Dienstag', wed: 'Mittwoch', thu: 'Donnerstag', fri: 'Freitag', sat: 'Samstag', sun: 'Sonntag' };
  const en: Record<DayKey, string> = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
  return (lang === 'de' ? de : en)[k];
}

export function formatSlots(d: DaySchedule, lang: Lang): string {
  if (d.closed || d.slots.length === 0) return lang === 'de' ? 'Geschlossen' : 'Closed';
  return d.slots.map((s) => `${s.open} – ${s.close}`).join(' · ');
}

export function slotLines(d: DaySchedule, lang: Lang): string[] {
  if (d.closed || d.slots.length === 0) return [lang === 'de' ? 'Geschlossen' : 'Closed'];
  return d.slots.map((s) => `${s.open} – ${s.close}`);
}

// Collapse consecutive days with identical hours into range rows like "Tue–Thu".
export interface GroupedRow {
  label: string;          // "Mon" | "Tue–Thu" | "Fri"
  hours: string;          // "11:30 – 14:30 · 17:00 – 22:30" or "Closed"
  closed: boolean;
}

export function groupWeekly(weekly: Record<DayKey, DaySchedule>, lang: Lang): GroupedRow[] {
  const shortDe: Record<DayKey, string> = { mon: 'Mo', tue: 'Di', wed: 'Mi', thu: 'Do', fri: 'Fr', sat: 'Sa', sun: 'So' };
  const shortEn: Record<DayKey, string> = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
  const short = lang === 'de' ? shortDe : shortEn;

  const signature = (d: DaySchedule): string =>
    (d.closed || d.slots.length === 0) ? 'CLOSED' : d.slots.map((s) => `${s.open}-${s.close}`).join('|');

  const rows: GroupedRow[] = [];
  let startIdx = 0;
  while (startIdx < DAY_KEYS.length) {
    const sig = signature(weekly[DAY_KEYS[startIdx]]);
    let endIdx = startIdx;
    while (endIdx + 1 < DAY_KEYS.length && signature(weekly[DAY_KEYS[endIdx + 1]]) === sig) {
      endIdx++;
    }
    const firstDay = DAY_KEYS[startIdx];
    const lastDay = DAY_KEYS[endIdx];
    const label = startIdx === endIdx ? short[firstDay] : `${short[firstDay]}–${short[lastDay]}`;
    const d = weekly[firstDay];
    rows.push({
      label,
      hours: formatSlots(d, lang),
      closed: d.closed || d.slots.length === 0,
    });
    startIdx = endIdx + 1;
  }
  return rows;
}
